import { Badge, type Tone } from "@/components/ui/Badge";
import { MedChronVersionStatus } from "@/constants";
import { formatDate } from "@/utils";
import { Button } from "@paribus/shared-ui";
import { AlertCircle, Download, FileText, Loader2 } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import type { CaseFileRecord, CaseFileStatus, FilesViewProps } from "./types";

const getDisplayName = (file: CaseFileRecord): string => {
  if (file.filename) return file.filename;
  if (file.bucket_url) return file.bucket_url.split("/").pop() ?? "Unknown file";
  return "Unknown file";
};

const HEADER_CELL_CLASS =
  "h-11 border-y border-border bg-surface-subtle px-6 text-left align-middle text-2xs font-semibold uppercase tracking-micro text-muted-foreground";

const BODY_CELL_CLASS = "border-b border-border-subtle px-6 py-4 align-middle text-sm";

interface FileStatusBadgeProps {
  status: CaseFileStatus | null;
}

interface StatusBadgeConfig {
  tone: Tone;
  label: string;
  icon?: ReactNode;
}

const STATUS_BADGE: Record<CaseFileStatus, StatusBadgeConfig> = {
  processing: {
    tone: "info",
    label: "Processing",
    icon: <Loader2 className="animate-spin" />,
  },
  failed: { tone: "danger", label: "Failed" },
  draft: { tone: "neutral", label: "Not included" },
  [MedChronVersionStatus.AI_REVIEWED]: { tone: "ai", label: "AI reviewed" },
  [MedChronVersionStatus.UNDER_HUMAN_REVIEW]: { tone: "info", label: "Under human review" },
  [MedChronVersionStatus.HUMAN_REVIEWED]: { tone: "success", label: "Human reviewed" },
};

function FileStatusBadge({ status }: FileStatusBadgeProps): ReactElement | null {
  if (status == null) return null;
  const { tone, label, icon } = STATUS_BADGE[status];
  return (
    <Badge tone={tone} icon={icon}>
      {label}
    </Badge>
  );
}

export function FilesView({
  files,
  loading,
  error,
  onPreviewClick,
  onDownloadClick,
  onDownloadAllClick,
}: FilesViewProps): ReactElement {
  if (loading && files.length === 0) {
    return (
      <div className="min-w-0 space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-14 animate-pulse rounded-xl border border-border bg-muted"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[240px] min-w-0 flex-col items-center justify-center rounded-2xl border border-destructive-border bg-destructive-subtle p-8 text-center">
        <AlertCircle className="mb-3 text-destructive-text" size={24} />
        <h3 className="text-base font-semibold text-destructive-text">Failed to load files</h3>
        <p className="mt-1 text-sm text-destructive-text">{error}</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex min-h-[280px] min-w-0 flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center">
        <FileText size={36} className="mb-3 text-neutral-400" />
        <h3 className="text-lg font-semibold text-neutral-900">No files found</h3>
        <p className="mt-2 max-w-md text-sm text-neutral-500">
          This case does not have any files available to preview in chronology yet.
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{files.length}</span> file
          {files.length === 1 ? "" : "s"} in chronology
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leadingIcon={<Download size={16} />}
          onClick={onDownloadAllClick}
          className="rounded-lg bg-primary-subtle text-primary-emphasis hover:bg-primary-subtle-hover hover:text-primary-emphasis"
        >
          Download all
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th scope="col" className={`${HEADER_CELL_CLASS} w-[44%]`}>
                File name
              </th>
              <th scope="col" className={`${HEADER_CELL_CLASS} w-[20%]`}>
                Upload date
              </th>
              <th scope="col" className={`${HEADER_CELL_CLASS} w-[24%]`}>
                Status
              </th>
              <th scope="col" className={`${HEADER_CELL_CLASS} w-[12%] text-center`}>
                Download
              </th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => {
              const fileName = getDisplayName(file);
              return (
                <tr key={file.file_id} className="hover:bg-surface-subtle">
                  <td className={`${BODY_CELL_CLASS} min-w-0`}>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => onPreviewClick(file)}
                      className="block h-auto w-full justify-start truncate p-0 text-left text-sm font-bold text-foreground hover:text-foreground"
                      title={fileName}
                    >
                      {fileName}
                    </Button>
                  </td>
                  <td className={`${BODY_CELL_CLASS} tabular-nums text-muted-foreground`}>
                    {formatDate(file.created_at)}
                  </td>
                  <td className={BODY_CELL_CLASS}>
                    <FileStatusBadge status={file.status} />
                  </td>
                  <td className={`${BODY_CELL_CLASS} text-center`}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Download file"
                      title="Download file"
                      onClick={() => onDownloadClick(file)}
                      className="h-8 w-8 p-0"
                    >
                      <Download size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
